import { useCallback, useEffect, useRef, useState } from "react";

import { quickbooksService } from "../../../../../api/quickbooks";

export default function useQuickbooksValidation({
  notify,
  openSingleSyncModal,
}) {
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [validationCustomer, setValidationCustomer] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [validationLoadingCustomerId, setValidationLoadingCustomerId] =
    useState(null);
  const validationPollTimeout = useRef(null);

  const clearValidationPoll = useCallback(() => {
    if (validationPollTimeout.current) {
      clearTimeout(validationPollTimeout.current);
      validationPollTimeout.current = null;
    }
  }, []);

  const handleValidationModalClose = useCallback(() => {
    setValidationModalOpen(false);
    setValidationCustomer(null);
    setValidationResults(null);
    setValidationLoadingCustomerId(null);
    clearValidationPoll();
  }, [clearValidationPoll]);

  const handleSyncStatusClick = useCallback(
    async (customer, statusMeta) => {
      if (!customer?.id) {
        return;
      }

      const label = (statusMeta?.label || "").toLowerCase();
      if (label === "sync needed" || label === "not synced") {
        openSingleSyncModal?.(customer);
        return;
      }

      setValidationCustomer(customer);
      setValidationResults(null);
      setValidationLoadingCustomerId(customer.id);
      setValidationModalOpen(true);

      try {
        const response = await quickbooksService.getCustomerById(customer.id, {
          include_quickbooks_sync_info: true,
          include_quickbooks_sync_results: true,
          include_quickbooks_connection: true,
        });

        const data = response.data;
        setValidationCustomer(data);
        setValidationResults(
          data?.sync_statuses?.last_validation_results || null
        );

        const lastSyncSuccess =
          data?.sync_statuses?.gl_last_sync_success ||
          data?.sync_statuses?.gl_last_sync_attempt;
        const validationAttempt = data?.sync_statuses?.last_validation_attempt;
        const needsPoll =
          Boolean(lastSyncSuccess) &&
          (!validationAttempt ||
            new Date(validationAttempt) < new Date(lastSyncSuccess));

        if (needsPoll) {
          clearValidationPoll();

          validationPollTimeout.current = setTimeout(async () => {
            try {
              const refreshedResponse =
                await quickbooksService.getCustomerById(customer.id, {
                  include_quickbooks_sync_info: true,
                  include_quickbooks_sync_results: true,
                  include_quickbooks_connection: true,
                });

              const refreshed = refreshedResponse.data;
              setValidationCustomer(refreshed);
              setValidationResults(
                refreshed?.sync_statuses?.last_validation_results || null
              );
              setValidationLoadingCustomerId(null);
            } catch {
              setValidationLoadingCustomerId(null);
            } finally {
              validationPollTimeout.current = null;
            }
          }, 4000);
        } else {
          setValidationLoadingCustomerId(null);
        }
      } catch {
        notify?.({
          open: true,
          message: "Unable to load validation report.",
          severity: "error",
        });
        setValidationModalOpen(false);
        setValidationCustomer(null);
        setValidationResults(null);
        setValidationLoadingCustomerId(null);
        clearValidationPoll();
      }
    },
    [clearValidationPoll, notify, openSingleSyncModal]
  );

  useEffect(() => {
    return () => {
      clearValidationPoll();
    };
  }, [clearValidationPoll]);

  return {
    validationModalOpen,
    validationCustomer,
    validationResults,
    validationLoadingCustomerId,
    handleSyncStatusClick,
    handleValidationModalClose,
  };
}